'use client'
import { useEffect, useMemo, useState } from 'react'
import {
  IconArrowBarDown,
  IconArrowBarRight,
  IconMoodSad
} from '@tabler/icons-react'
import clsx from 'clsx'
import Image from 'next/image'
import { usePathname, useSearchParams } from 'next/navigation'
import { useSession } from 'next-auth/react'

import { BannerListing, BannerTypes } from 'modules/content'
import {
  CustomerTypes,
  extractCustomerFromClientSession
} from 'modules/customer'
import {
  Brand,
  CategoryTags,
  FilterChangeProvider,
  getNextEntityPage,
  getProperProductForm,
  ListingCategoryType,
  ProductCard,
  ProductMetaTypes,
  ProductTypes,
  useFilterChange,
  useInfiniteScroll,
  viewProductList
} from 'modules/products'
import { SeoListName, seoListNameDynamic } from 'shared/analytics'
import BackToTopButton from 'shared/ui/BackToTopButton/BackToTopButton'
import Breadcrumbs from 'shared/ui/Breadcrumbs'
import IconButton from 'shared/ui/IconButton'
import InfiniteScrollAnchor from 'shared/ui/InfiniteScrollAnchor'
import LoadingIndicator from 'shared/ui/LoadingIndicator/LoadingIndicator'
import LoadingOverlay from 'shared/ui/LoadingOverlay'
import { theme } from 'shared/ui/utils/designTokens'
import { PRODUCTS_LISTING_LIMIT } from 'shared/utils/common'
import { PATHS } from 'shared/utils/paths'
import { isNotNilOrEmpty } from 'shared/utils/ramda'
import { addTestId } from 'shared/utils/tests'

import CategoryFilters from '../submodules/filters/components/CategoryFilters'
import Filters from '../submodules/filters/components/Filters'
import HorizontalCategoryFilters from '../submodules/filters/components/HorizontalCategoryFilters'
import MobileFilters from '../submodules/filters/components/MobileFilters'
import { FilterDictionary } from '../submodules/filters/types'
import { findMainCategory, getFlattenCategories } from '../utils'

import styles from './CategoryPage.module.scss'

interface CategoryPageProps {
  products: ProductTypes[]
  banners: BannerTypes[]
  categories: ListingCategoryType[]
  category: ListingCategoryType | null
  productsMeta: ProductMetaTypes
  brands: Brand[]
  tags: FilterDictionary[]
  deliveryDates: string[]
  slug: string
  distributionCenterId: string
  token?: string
  breadcrumbs: { label: string; path: string }[]
}

const CategoryPageContent = ({
  products,
  categories,
  category,
  productsMeta,
  brands,
  tags,
  banners,
  deliveryDates,
  slug,
  distributionCenterId,
  token,
  breadcrumbs
}: CategoryPageProps) => {
  const [allCategories, setAllCategories] = useState<ListingCategoryType[]>([])

  const [isLoading, setIsLoading] = useState(false)
  const [isFilterVertical, setIsFilterVertical] = useState(false)

  const asPath = usePathname()
  const searchParams = useSearchParams()
  const query = useMemo(
    () => Object.fromEntries(searchParams.entries()),
    [searchParams]
  )
  const { lastFilterChange } = useFilterChange()
  const { ref, productsData, isFetching } = useInfiniteScroll({
    products,
    query,
    slug,
    distributionCenterId,
    token,
    getNextPageCallback: getNextEntityPage,
    limit: PRODUCTS_LISTING_LIMIT
  })

  const { total } = productsMeta

  const mainCategory = findMainCategory(categories, category)

  const session = useSession()
  const user: CustomerTypes | undefined = extractCustomerFromClientSession(session)
  const listName = seoListNameDynamic(
    SeoListName.CATEGORY_PAGE_LIST_NAME,
    category?.name || ''
  )

  const toggleVerticalFiltersLayout = (isVertical: boolean) => {
    setIsFilterVertical(isVertical)
  }

  useEffect(() => {
    if (lastFilterChange) {
      setIsLoading(true)
    } else {
      setIsLoading(false)
    }
  }, [lastFilterChange])

  useEffect(() => {
    setIsLoading(false)
  }, [asPath, searchParams])

  useEffect(() => {
    if (session?.status !== 'loading' && isNotNilOrEmpty(products)) {
      viewProductList({
        user,
        products,
        listName
      })
    }
  }, [session, listName, products])

  const backLinkDetails = useMemo(() => {
    if (category?.depth === 1) {
      return {
        label: 'Lista kategorii',
        path: PATHS.categories
      }
    } else {
      const parentCategory = allCategories.find(
        (cat: ListingCategoryType) => cat.id === category?.parentId
      )

      if (parentCategory) {
        return {
          label: parentCategory.name,
          path: parentCategory?.totalSlug || ''
        }
      }

      return { label: '', path: '' }
    }
  }, [category, allCategories])

  useEffect(() => {
    if (isNotNilOrEmpty(category)) {
      setAllCategories(getFlattenCategories(categories))
    }
  }, [categories, category])

  const categoriesToDisplay = useMemo(() => {
    const hasVisibleSubcategories = isNotNilOrEmpty(
      category?.subCategories?.filter(c => c.isVisible)
    )

    if (hasVisibleSubcategories) {
      if (category?.isVisible) {
        return category?.subCategories?.filter(
          cat => cat.isVisible
        ) as ListingCategoryType[]
      } else {
        return category?.subCategories as ListingCategoryType[]
      }
    } else {
      return allCategories.filter((cat: ListingCategoryType) => {
        const sameParent = cat.parentId === category?.parentId
        const isVisible = cat.isVisible
        const isSelectedCategory = cat.id === category?.id

        return sameParent && (isVisible || isSelectedCategory)
      })
    }
  }, [category, allCategories])

  const parentCategoryIds = category?.parentCategoryIds || []
  const bannerCategoryIds = [...parentCategoryIds, category?.id]

  const filteredBanners = banners.filter(banner => {
    const productCategoryId = banner?.productCategoryId

    return (
      productCategoryId === null ||
      bannerCategoryIds.includes(productCategoryId)
    )
  })

  return (
    <div className={styles.wrapper}>
      <BackToTopButton target='*[data-title="top"]' />
      {isLoading && <LoadingOverlay />}
      <div className={styles.breadcrumbsWrapper}>
        <Breadcrumbs breadcrumbs={breadcrumbs} />
      </div>
      {/* Web content */}
      <div className={styles.onlyWebContent}>
        {isNotNilOrEmpty(filteredBanners) ? (
          <BannerListing banners={filteredBanners} placement='category_page' />
        ) : (
          <div className={styles.titleBanner}>
            <h1 className={styles.titleBannerHeading} {...addTestId('category-page-title')}>
              {category?.title || category?.name}
            </h1>
            <span className={styles.titleBannerProductsNumber}>
              ({getProperProductForm(total)})
            </span>
          </div>
        )}

        <CategoryTags categories={categories} mainCategory={category} />
      </div>
      {/* Mobile content */}
      <span data-title='top' />
      <div className={styles.tabletContent}>
        <div className={styles.header}>
          <div className={styles.categoryIconWrapper}>
            {mainCategory?.icon?.path && (
              <Image
                alt={mainCategory?.name || 'Kategoria'}
                src={mainCategory?.icon?.path || ''}
                width={24}
                height={24}
              />
            )}
          </div>
          <div className={styles.mobileCategoryNameWrapper}>
            <div className={styles.titleWrapper}>
              <h1 className={styles.titleMobile} {...addTestId('category-page-title')}>
                {category?.title || category?.name}
              </h1>
              <p className={styles.numberOfProducts}>
                ({getProperProductForm(total)})
              </p>
            </div>
          </div>
        </div>
        <MobileFilters
          deliveryDates={deliveryDates}
          tags={tags}
          brands={brands}
        />
      </div>
      <div
      className={clsx(styles.contentWrapper, {
        [styles.contentWrapperVertical]: isFilterVertical
      })}
    >
        {/* Filters visible on WEB only */}
        {isFilterVertical && (
          <div className={styles.onlyWebContent}>
            <CategoryFilters
              backLinkDetails={backLinkDetails}
              categoriesToDisplay={categoriesToDisplay}
              category={category}
              deliveryDates={deliveryDates}
            />
          </div>
        )}
        <div className={styles.listingWrapper}>
          <div className={styles.onlyWebContent}>
            <div className={styles.headerContent}>
              <div className={styles.header}>
                <div className={styles.titleWrapper}>
                  <p className={styles.numberOfProducts}>
                    Produkty w kategorii: {total}
                  </p>
                </div>
                <div className={styles.categorySwitchButtonWrapper}>
                  <p className={styles.categorySwitchTitle}>Układ kategorii:</p>
                  <IconButton
                    variant='transparent'
                    size='medium'
                    disabled={false}
                    className={clsx({
                      [styles.categorySwitchButtonActive]: isFilterVertical
                    })}
                    onClick={() => toggleVerticalFiltersLayout(true)}
                    aria-label='Układ kategorii: poziomy'
                  >
                    <IconArrowBarRight></IconArrowBarRight>
                  </IconButton>
                  <IconButton
                    variant='transparent'
                    size='medium'
                    disabled={false}
                    className={clsx({
                      [styles.categorySwitchButtonActive]: !isFilterVertical
                    })}
                    onClick={() => toggleVerticalFiltersLayout(false)}
                    aria-label='Układ kategorii: pionowy'
                  >
                    <IconArrowBarDown></IconArrowBarDown>
                  </IconButton>
                </div>
              </div>
              {!isFilterVertical && (
                <HorizontalCategoryFilters
                  backLinkDetails={backLinkDetails}
                  categoriesToDisplay={categoriesToDisplay}
                  category={category}
                />
              )}
              <Filters
                deliveryDates={deliveryDates}
                tags={tags}
                brands={brands}
              />
            </div>
          </div>

          {/* Common content - listing */}
          <>
            {productsData && productsData.length > 0 ? (
              <div className={styles.productsListWrapper}>
                <div
                  className={clsx(styles.content, {
                    [styles.contentVertical]: isFilterVertical
                  })}
                  role='list'
                  aria-label='Lista produktów'
                >
                  {productsData.map((product: ProductTypes, idx: number) => {
                    return (
                      <ProductCard
                        productPath={product?.slug || ''}
                        key={product.id + product.slug + idx.toString()}
                        product={product}
                        ageLimitMetCategory={category?.ageLimitMet}
                        seoButtonPlace={asPath}
                        listName={seoListNameDynamic(
                          SeoListName.CATEGORY_PAGE_LIST_NAME,
                          category?.name || ''
                        )}
                        idxOnList={idx}
                        id={`category_page_product_${product.id}`}
                      />
                    )
                  })}
                  <InfiniteScrollAnchor ref={ref} />
                </div>
                {isFetching && (
                  <div className={styles.circularProgressWrapper}>
                    <LoadingIndicator size={80} />
                  </div>
                )}
              </div>
            ) : isFetching ? (
              <div className={styles.circularProgressWrapper}>
                <LoadingIndicator size={80} />
              </div>
            ) : (
              <div className={styles.emptyStateWrapper}>
                <IconMoodSad
                  width={120}
                  size={120}
                  strokeWidth={1}
                  color={theme.colors.text.disabled}
                />
                <div>
                  <div className={styles.emptyStateTitle}>Brak wyników</div>
                  <div className={styles.emptyStateDescription}>
                    Niestety nie udało nam się znaleźć odpowiednich produktów.
                    <br />
                    Spróbuj dostosować filtry lub sprawdź w innej kategorii.
                  </div>
                </div>
              </div>
            )}
          </>
        </div>
      </div>
      {category?.seoDescription && (
        <div className={styles.seoContainer}>
          <div className={styles.seoTitle}>Opis kategorii</div>
          <div dangerouslySetInnerHTML={{ __html: category?.seoDescription }} />
        </div>
      )}
    </div>
  )
}

const CategoryPage = (props: CategoryPageProps) => {
  return (
    <FilterChangeProvider>
      <CategoryPageContent {...props} />
    </FilterChangeProvider>
  )
}

export default CategoryPage
